import React, { useState } from 'react';
import { Modal, Input, Button, Space, Typography, Alert, Spin } from 'antd';
import { RobotOutlined, SendOutlined } from '@ant-design/icons';
import { useAiAssistMutation } from '../../../../store/api/templateApi';
import { toast } from '../../../../components/common/Toast';

const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;

const promptExamples = [
  'Generate 10 security compliance questions for a software vendor assessment',
  'Create questions about data protection and GDPR compliance',
  'Generate financial capability assessment questions with budget categories',
  'Create technical infrastructure questions with table for server specifications',
];

export default function AIAssistant({ open, onClose, templateId, onQuestionsGenerated }) {
  const [prompt, setPrompt] = useState('');
  const [aiAssist, { isLoading }] = useAiAssistMutation();

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.warning('Please enter a prompt');
      return;
    }

    try {
      const result = await aiAssist({
        id: templateId,
        prompt,
      }).unwrap();

      toast.success('Questions generated');
      onQuestionsGenerated(result);
      setPrompt('');
      onClose();
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error('Failed to generate questions');
    }
  };

  const handleExampleClick = (example) => {
    setPrompt(example);
  };

  return (
    <Modal
      title={
        <Space>
          <RobotOutlined style={{ color: '#8B5CF6', fontSize: '20px' }} />
          <Title level={4} style={{ margin: 0 }}>
            AI Assistant
          </Title>
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="generate"
          type="primary"
          icon={<SendOutlined />}
          loading={isLoading}
          onClick={handleGenerate}
          style={{ background: '#8B5CF6', borderColor: '#8B5CF6' }}
        >
          Generate Questions
        </Button>,
      ]}
      width={700}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Alert
          message="Powered by GPT-4"
          description="Describe what you need and AI will generate structured questions for your template."
          type="info"
          showIcon
        />

        <div>
          <Text strong style={{ display: 'block', marginBottom: '8px' }}>
            What would you like to generate?
          </Text>
          <TextArea
            rows={6}
            placeholder="Example: Generate 10 capability assessment questions for software vendors, including questions about certifications, data protection, and incident response"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={isLoading}
          />
        </div>

        <div>
          <Text strong style={{ display: 'block', marginBottom: '12px' }}>
            Example Prompts:
          </Text>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {promptExamples.map((example, index) => (
              <Button
                key={index}
                type="text"
                onClick={() => handleExampleClick(example)}
                style={{
                  textAlign: 'left',
                  height: 'auto',
                  padding: '8px 12px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  width: '100%',
                }}
                disabled={isLoading}
              >
                <Text style={{ fontSize: '13px' }}>{example}</Text>
              </Button>
            ))}
          </Space>
        </div>

        {isLoading && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
            <Paragraph type="secondary" style={{ marginTop: '12px' }}>
              AI is generating questions...
            </Paragraph>
          </div>
        )}

        <div style={{ background: '#F9F5FF', padding: '12px', borderRadius: '8px' }}>
          <Text strong style={{ fontSize: '13px' }}>
            Tips for Best Results:
          </Text>
          <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '20px' }}>
            <li>
              <Text style={{ fontSize: '13px' }}>Be specific about the domain (e.g., security, finance, operations)</Text>
            </li>
            <li>
              <Text style={{ fontSize: '13px' }}>Mention desired question count (e.g., "10 questions")</Text>
            </li>
            <li>
              <Text style={{ fontSize: '13px' }}>Specify field types if needed (e.g., "with a table for specs")</Text>
            </li>
          </ul>
        </div>
      </Space>
    </Modal>
  );
}
